import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const SECTIONS = [
  {
    title: "Product Information",
    summary: "Commodity, SKU, HS code, target spec",
    detail: [
      "Cotton Hand Towels — 600 GSM",
      "Composition: 100% Combed Cotton",
      "Dimensions: 50 × 90 cm · ±2%",
      "HS Code: 6302.60.00",
    ],
    status: "complete",
  },
  {
    title: "Commercial Terms",
    summary: "Quantity, Incoterm, payment, target delivery",
    detail: [
      "Quantity: 24,000 pcs",
      "Incoterm: FOB Izmir Port",
      "Payment: LC at Sight · 30% advance",
      "Target delivery: 18 Mar 2026",
    ],
    status: "complete",
  },
  {
    title: "Packaging",
    summary: "Polybag + Carton · 12 pcs / carton",
    detail: [
      "Inner: Sealed polybag with brand label",
      "Master: 5-ply carton · 60 × 40 × 35 cm",
      "Stack rating: 2× pallet height",
      "Pallet: ISPM-15 heat-treated",
    ],
    status: "complete",
  },
  {
    title: "Quality Requirements",
    summary: "Tolerances, defects, sampling plan",
    detail: [
      "AQL 2.5 · ISO 2859-1",
      "Color tolerance ΔE ≤ 1.5",
      "Pre-production sample required",
      "GSM tolerance ±3%",
    ],
    status: "complete",
  },
  {
    title: "Certificates",
    summary: "OEKO-TEX 100, GOTS",
    detail: ["OEKO-TEX 100 (Class I)", "GOTS Organic Certified", "REACH compliant"],
    status: "complete",
  },
  {
    title: "Inspection",
    summary: "Pre-shipment 3rd-party inspection",
    detail: [
      "SGS / Intertek / Bureau Veritas",
      "Pre-shipment + during-production",
      "Report required before BL release",
    ],
    status: "complete",
  },
  {
    title: "Shipping Preferences",
    summary: "Sea freight · FOB Izmir → Hamburg",
    detail: [
      "Container: 40' HQ (×2)",
      "Routing: Izmir → Hamburg",
      "Insurance: All-risk (CIF terms acceptable)",
    ],
    status: "complete",
  },
  {
    title: "Supporting Documents",
    summary: "3 files attached",
    detail: ["Tech_Pack_v3.pdf", "Color_Reference.png", "Brand_Label_Spec.pdf"],
    status: "complete",
  },
  {
    title: "Buyer Notes",
    summary: "Additional context for manufacturers",
    detail: [
      "Recurring annual program — 4 shipments / year",
      "Preference for OEKO-TEX certified mills in Denizli or Bursa",
    ],
    status: "in-progress",
  },
];

const COMPLETED = SECTIONS.filter((s) => s.status === "complete").length;
const RECENT_ACTIVITY = [
  { action: "Edited Commercial Terms", time: "2m" },
  { action: "Validated HS code", time: "7m" },
  { action: "Attached Tech_Pack_v3.pdf", time: "14m" },
];
const RFQ_TIMELINE = [
  "RFQ Created",
  "Product Added",
  "Commercial Terms Completed",
  "Documents Uploaded",
  "Waiting For Final Review",
];
const AI_REVIEW = [
  { label: "Packaging information looks complete", tone: "ok" },
  { label: "HS Code validated", tone: "ok" },
  { label: "Buyer Notes recommended", tone: "warn" },
  { label: "Missing target delivery date", tone: "warn" },
];
const RECENT_CHANGES = [
  { label: "Buyer Notes draft updated", time: "Now" },
  { label: "Certificates reviewed", time: "12m" },
  { label: "Documents reordered", time: "24m" },
  { label: "Incoterm confirmed", time: "41m" },
];

function SectionCard({
  section,
  index,
  open,
  setOpen,
  className = "",
  detailColumns = "sm:grid-cols-2",
  large = false,
}) {
  const isOpen = open === index;
  const sectionNo = String(index + 1).padStart(2, "0");

  return (
    <div
      className={`rounded-[22px] border border-black/[0.05] bg-white shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-black/[0.08] hover:shadow-card ${
        isOpen ? "border-black/[0.08] shadow-card" : ""
      } ${className}`}
      data-testid={`builder-section-${index}`}
    >
      <button
        onClick={() => setOpen(isOpen ? -1 : index)}
        className={`w-full text-left ${large ? "px-5 py-5 sm:px-6" : "px-4 py-4 sm:px-5"}`}
      >
        <div className="flex items-start gap-3">
          <span
            className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] font-mono text-[10px] font-semibold tracking-[0.12em] transition-colors ${
              isOpen ? "bg-[#F0F3F8] text-black/60" : "bg-[#F3F4F6] text-black/42"
            }`}
          >
            {sectionNo}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`font-display font-semibold tracking-tight text-[#171717] ${
                      large ? "text-[16px]" : "text-[15px]"
                    }`}
                  >
                    {section.title}
                  </span>
                  {section.status === "complete" ? (
                    <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2 py-[3px] font-mono text-[8.5px] tracking-[0.18em] text-emerald-700 transition-transform duration-200 group-hover:scale-[1.02]">
                      COMPLETE
                    </span>
                  ) : (
                    <span className="rounded-full border border-amber-100 bg-amber-50 px-2 py-[3px] font-mono text-[8.5px] tracking-[0.18em] text-amber-700 transition-transform duration-200 group-hover:scale-[1.02]">
                      IN PROGRESS
                    </span>
                  )}
                </div>
                <p className={`mt-1 text-black/42 ${large ? "text-[12.5px]" : "text-[12px]"}`}>
                  {section.summary}
                </p>
              </div>
              <ChevronDown
                className={`mt-0.5 h-4 w-4 shrink-0 text-black/28 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </div>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className={`${large ? "px-5 pb-5 sm:px-6" : "px-4 pb-4 sm:px-5"}`}>
              <div className="ml-[52px] rounded-[16px] border border-black/[0.04] bg-[#FAFBFC] p-4">
                <ul className={`grid gap-x-6 gap-y-2 ${detailColumns}`}>
                  {section.detail.map((d) => (
                    <li
                      key={d}
                      className="flex items-start gap-2 text-[12.5px] leading-relaxed text-black/68"
                    >
                      <span className="mt-[7px] inline-flex h-1 w-1 shrink-0 rounded-full bg-black/28" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Builder() {
  const [open, setOpen] = useState(0);
  const product = SECTIONS[0];
  const commercial = SECTIONS[1];
  const packaging = SECTIONS[2];
  const quality = SECTIONS[3];
  const certificates = SECTIONS[4];
  const inspection = SECTIONS[5];
  const shipping = SECTIONS[6];
  const documents = SECTIONS[7];
  const buyerNotes = SECTIONS[8];

  return (
    <section
      id="builder"
      data-testid="builder-section"
      className="relative py-24 sm:py-32 border-t border-black/[0.06] bg-[#FAFAFB]"
    >
      <div className="absolute inset-0 bg-grid-fine opacity-50 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center rounded-full border border-black/[0.06] bg-white px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-black/55">
            The RFQ Builder
          </span>
          <h2 className="mt-4 font-display font-black tracking-[-0.035em] text-[34px] sm:text-[42px] leading-[1.02] text-[#111111]">
            Every requirement,
            <span className="text-black/40"> in one calm workspace.</span>
          </h2>
          <p className="mt-4 text-[14px] leading-relaxed text-black/52 max-w-xl">
            Structured sections, clear status and no unnecessary visual noise.
          </p>
        </div>

        <div className="mt-10 rounded-[32px] border border-black/[0.06] bg-white shadow-os overflow-hidden">
          <div className="flex items-center justify-between border-b border-black/[0.05] px-5 py-3 sm:px-6">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
              </div>
              <span className="font-mono text-[10.5px] tracking-[0.18em] text-black/45">
                rfq-0241 / builder
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline-flex rounded-full border border-black/[0.05] bg-[#F6F8FA] px-2.5 py-1 font-mono text-[10px] tracking-[0.18em] text-black/55">
                AUTOSAVE ON
              </span>
              <span className="inline-flex rounded-full border border-black/[0.05] bg-[#F6F8FA] px-2.5 py-1 font-mono text-[10px] tracking-[0.18em] text-black/55">
                DRAFT
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.45fr)_minmax(300px,0.62fr)] lg:items-stretch">
            <div className="bg-[#FCFCFD] p-4 sm:p-5">
              <div className="grid gap-3">
                <SectionCard
                  section={product}
                  index={0}
                  open={open}
                  setOpen={setOpen}
                  large
                  className="group"
                />

                <SectionCard
                  section={commercial}
                  index={1}
                  open={open}
                  setOpen={setOpen}
                  className="group"
                />

                <div className="grid gap-3 lg:grid-cols-2">
                  <SectionCard
                    section={packaging}
                    index={2}
                    open={open}
                    setOpen={setOpen}
                    detailColumns="grid-cols-1"
                    className="group"
                  />
                  <SectionCard
                    section={quality}
                    index={3}
                    open={open}
                    setOpen={setOpen}
                    detailColumns="grid-cols-1"
                    className="group"
                  />
                </div>

                <SectionCard
                  section={certificates}
                  index={4}
                  open={open}
                  setOpen={setOpen}
                  className="group"
                />

                <div className="grid gap-3 lg:grid-cols-2">
                  <SectionCard
                    section={inspection}
                    index={5}
                    open={open}
                    setOpen={setOpen}
                    detailColumns="grid-cols-1"
                    className="group"
                  />
                  <SectionCard
                    section={shipping}
                    index={6}
                    open={open}
                    setOpen={setOpen}
                    detailColumns="grid-cols-1"
                    className="group"
                  />
                </div>

                <SectionCard
                  section={documents}
                  index={7}
                  open={open}
                  setOpen={setOpen}
                  large
                  className="group"
                />

                <div
                  className="rounded-[22px] border border-black/[0.05] bg-white shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-black/[0.08] hover:shadow-card"
                  data-testid="builder-section-8"
                >
                  <button
                    onClick={() => setOpen(open === 8 ? -1 : 8)}
                    className="w-full px-5 py-4 text-left"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] font-mono text-[10px] font-semibold tracking-[0.12em] ${
                          open === 8 ? "bg-[#F2F4F7] text-black/55" : "bg-[#F3F4F6] text-black/42"
                        }`}
                      >
                        09
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-display text-[15px] font-semibold tracking-tight text-[#171717]">
                                {buyerNotes.title}
                              </span>
                              <span className="rounded-full border border-amber-100 bg-amber-50 px-2 py-[3px] font-mono text-[8.5px] tracking-[0.18em] text-amber-700">
                                IN PROGRESS
                              </span>
                            </div>
                            <p className="mt-1 text-[12px] text-black/42">{buyerNotes.summary}</p>
                          </div>
                          <ChevronDown
                            className={`mt-0.5 h-4 w-4 shrink-0 text-black/28 transition-transform duration-200 ${
                              open === 8 ? "rotate-180" : ""
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {open === 8 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5">
                          <div className="ml-[52px] rounded-[16px] border border-black/[0.04] bg-[#FAFBFC] p-4">
                            <div className="min-h-[132px] rounded-[14px] border border-black/[0.05] bg-white px-4 py-3 text-[12.5px] leading-relaxed text-black/58">
                              Recurring annual program. Preference for OEKO-TEX certified mills in Denizli
                              or Bursa. Add buyer expectations, quality notes and commercial context before
                              sending to manufacturers.
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="grid items-stretch gap-3 lg:grid-cols-[1.05fr_0.95fr]">
                  <div className="flex h-full flex-col rounded-[22px] border border-black/[0.05] bg-white p-5 shadow-soft">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-mono text-[9px] tracking-[0.24em] text-black/38">
                          RFQ TIMELINE
                        </p>
                        <h3 className="mt-2 font-display text-[16px] font-semibold tracking-tight text-[#171717]">
                          Builder progress
                        </h3>
                      </div>
                      <span className="rounded-full bg-[#F4F6FA] px-2 py-1 font-mono text-[9px] tracking-[0.16em] text-black/48">
                        LIVE
                      </span>
                    </div>
                    <div className="mt-4 grid flex-1 content-start gap-2">
                      {RFQ_TIMELINE.slice(0, 4).map((item, idx) => (
                        <div
                          key={item}
                          className="flex items-center gap-3 rounded-[16px] border border-black/[0.04] bg-[#FAFBFC] px-3 py-2.5"
                        >
                          <span
                            className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ${
                              idx < 3 ? "bg-[#F0F4FF] text-[#0044FF]" : "bg-amber-50 text-amber-700"
                            }`}
                          >
                            {idx + 1}
                          </span>
                          <span className="text-[12px] text-black/70">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex h-full flex-col rounded-[22px] border border-black/[0.05] bg-white p-5 shadow-soft">
                    <p className="font-mono text-[9px] tracking-[0.24em] text-black/38">
                      READINESS
                    </p>
                    <div className="mt-3 flex flex-1 flex-col rounded-[18px] border border-black/[0.04] bg-[#FAFBFC] p-4">
                      <div className="flex items-end justify-between gap-3">
                        <div>
                          <p className="text-[11px] text-black/42">Overall Score</p>
                          <p className="mt-2 font-display text-[30px] font-black leading-none text-[#111111]">
                            92%
                          </p>
                        </div>
                        <span className="rounded-full bg-white px-2 py-1 font-mono text-[9px] tracking-[0.16em] text-black/48">
                          READY
                        </span>
                      </div>
                      <div className="mt-4 h-2 overflow-hidden rounded-full bg-black/[0.06]">
                        <div className="shimmer h-full w-[92%] rounded-full bg-gradient-to-r from-[#0044FF] to-emerald-500" />
                      </div>
                      <div className="mt-4 space-y-2">
                        {AI_REVIEW.slice(0, 3).map(({ label, tone }) => (
                          <div key={label} className="flex items-start gap-2 text-[11.5px] text-black/66">
                            <span className={tone === "ok" ? "text-emerald-700" : "text-amber-700"}>
                              {tone === "ok" ? "✓" : "!"}
                            </span>
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <aside className="border-t lg:border-t-0 lg:border-l border-black/[0.05] bg-[#FCFCFD] p-4">
              <div className="sticky top-24 flex flex-col gap-3 lg:min-h-[calc(100vh-10rem)]">
                <div className="rounded-[20px] border border-black/[0.05] bg-white p-4">
                  <div className="font-mono text-[9px] tracking-[0.24em] text-black/38">
                    COMPLETION
                  </div>
                  <div className="mt-2 flex items-end justify-between gap-3">
                    <div className="font-display text-[32px] font-black leading-none tracking-tight">
                      92<span className="text-[16px] text-black/35 font-semibold">%</span>
                    </div>
                    <span className="rounded-full bg-[#F4F6FA] px-2 py-1 font-mono text-[9px] tracking-[0.16em] text-black/50">
                      {COMPLETED}/{SECTIONS.length} DONE
                    </span>
                  </div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-black/[0.06]">
                    <div className="shimmer h-full w-[92%] rounded-full bg-gradient-to-r from-[#0044FF] to-emerald-500" />
                  </div>
                </div>

                <div className="rounded-[20px] border border-black/[0.05] bg-white p-4">
                  <div className="font-mono text-[9px] tracking-[0.24em] text-black/38">
                    WORKSPACE STATUS
                  </div>
                  <div className="mt-3 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[12px] font-medium text-black/76">Draft</p>
                        <p className="text-[11px] text-black/45">Visible only to your workspace</p>
                      </div>
                      <div className="flex items-center gap-1.5 pt-0.5">
                        <span className="inline-flex h-2 w-2 rounded-full bg-amber-500 pulse-dot" />
                        <span className="text-[12px] font-semibold text-black/80">Open</span>
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[12px] font-medium text-black/76">Autosave</p>
                        <p className="text-[11px] text-black/45">Last sync 4 seconds ago</p>
                      </div>
                      <span className="text-[12px] font-semibold text-emerald-700">Enabled</span>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[12px] font-medium text-black/76">Validation</p>
                        <p className="text-[11px] text-black/45">Section checks running</p>
                      </div>
                      <span className="text-[12px] font-semibold text-black/72">Active</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-[20px] border border-black/[0.05] bg-white p-4">
                  <span className="font-mono text-[9px] tracking-[0.24em] text-black/38">
                    RECENT ACTIVITY
                  </span>
                  <ul className="mt-3 space-y-2">
                    {RECENT_ACTIVITY.map(({ action, time }) => (
                      <li
                        key={action}
                        className="flex items-start justify-between gap-3 rounded-xl bg-[#FAFAFB] px-3 py-2 text-[11.5px] leading-snug"
                      >
                        <span className="text-black/70">{action}</span>
                        <span className="shrink-0 font-mono text-[10px] text-black/35">{time}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-[20px] border border-black/[0.05] bg-white p-4">
                  <span className="font-mono text-[9px] tracking-[0.24em] text-black/38">
                    NEXT STEP
                  </span>
                  <div className="mt-3 grid gap-2">
                    <button className="btn-primary w-full text-[12px]">Review RFQ</button>
                    <button className="btn-secondary w-full text-[12px]">Launch CommodityBid™</button>
                    <button className="btn-secondary w-full text-[12px]">Send to Manufacturers</button>
                  </div>
                </div>

                <div className="flex-1 rounded-[20px] border border-black/[0.05] bg-white p-4">
                  <span className="font-mono text-[9px] tracking-[0.24em] text-black/38">
                    SUPPLIER REACH
                  </span>
                  <div className="mt-3 flex h-[calc(100%-1.5rem)] min-h-[220px] flex-col rounded-[16px] border border-black/[0.04] bg-[#FAFBFC] p-4">
                    <div>
                      <p className="font-display text-[28px] font-black leading-none text-[#111111]">12</p>
                      <p className="mt-1 text-[11px] text-black/45">Verified Manufacturers</p>
                    </div>
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between rounded-xl bg-white px-3 py-2">
                        <span className="text-[11.5px] text-black/65">Region</span>
                        <span className="font-mono text-[10px] text-black/40">Turkey</span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-white px-3 py-2">
                        <span className="text-[11.5px] text-black/65">Avg. response</span>
                        <span className="text-[11.5px] font-medium text-black/78">18h</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}
